import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate } from 'react-router-dom';
import {
  TVehiculeCheckSchema,
  vehiculeCheckSchema,
} from '@/schemas/vehicule-check/vehicule-check.schema';
import { VehiculeCheckRequestManagement } from '../components/VehiculeCheckRequestManagement';
import { useCreateVehiculeCheck } from '@/hooks/vehicule-check.hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionUserType,
  VehiculeCheckProgressStatus,
} from '@/api/entities/vehicule-check';
import Loader from '@/components/Loading/Loader';
import { parseEmails } from '@/utils/string.utils';

export const CreateVehiculeCheckRequestPage = () => {
  const { t } = useTranslation('inspection');
  const navigate = useNavigate();

  const {
    watch: watchVehiculeCheck,
    control: vehiculeCheckControl,
    register: registerVehiculeCheck,
    setValue: setVehiculeCheckValue,
    setError: setVehiculeCheckError,
    handleSubmit: handleSubmitVehiculeCheck,
    formState: {
      errors: vehiculeCheckErrors,
      isSubmitting: isSubmittingVehiculeCheck,
    },
  } = useForm<TVehiculeCheckSchema>({
    resolver: zodResolver(vehiculeCheckSchema),
  });

  const {
    mutate: mutateCreateVehiculeCheck,
    isPending: isPendingCreateVehiculeCheck,
  } = useCreateVehiculeCheck({
    t,
    handleSuccess: () => {
      navigate('/vehicule-check-requests');
    },
  });

  const onSubmitVehiculeCheck = async (vehiculeCheck: TVehiculeCheckSchema) => {
    vehiculeCheck = {
      ...vehiculeCheck,
      additionalEmails: parseEmails({
        inputEmails: vehiculeCheck?.additionalEmails,
        excludeEmails: [vehiculeCheck?.individualEmail],
      }),
      emailIsToSend: true,
      actionUserType: ActionUserType.INDIVIDUAL,
      progressStatus: VehiculeCheckProgressStatus.REQUEST_CREATED,
    };
    mutateCreateVehiculeCheck(vehiculeCheck);
  };

  return (
    <>
      <PageHeader
        backToLink={history?.length > 1 ? -1 : '/vehicule-check-requests'}
        title={t('common:requestAnInspection')}
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem
              text={t('common:inspectionsList')}
              linkTo="/vehicule-check-requests"
            />
            <BreadcrumbItem text={t('common:inspection')} active />
          </BreadcrumbList>
        }
      />
      <div>
        <VehiculeCheckRequestManagement<TVehiculeCheckSchema>
          t={t}
          watch={watchVehiculeCheck}
          control={vehiculeCheckControl}
          register={registerVehiculeCheck}
          setValue={setVehiculeCheckValue}
          setError={setVehiculeCheckError}
          errors={vehiculeCheckErrors}
          submitDisabled={
            isSubmittingVehiculeCheck || isPendingCreateVehiculeCheck
          }
          onSubmit={handleSubmitVehiculeCheck(onSubmitVehiculeCheck)}
        />
      </div>
      {(isSubmittingVehiculeCheck || isPendingCreateVehiculeCheck) && (
        <Loader />
      )}
    </>
  );
};
